import { useState, useEffect } from "react";

/* ─── logged-in account mock  ─── */
const LOGGED_IN = {
  name: "Alex Morgan",
  email: "a.morgan@gascrm.co.za",
  role: "Operations Manager",
  phone: "+27 82 444 0011",
  region: "Gauteng",
  avatar: "AM",
  language: "en-ZA",
  timezone: "Africa/Johannesburg",
  currency: "ZAR",
  twoFactor: true,
  emailNotifs: true,
  smsNotifs: false,
  weeklyReport: true,
  overdueAlerts: true,
  newOrderAlerts: false,
  theme: "dark",
  compactMode: false,
  defaultView: "dashboard",
};

function Section({ title, children, icon }) {
  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="ph">
        <i className={`bi ${icon}`} style={{ color: "var(--flame)", fontSize: 14 }} />
        <span className="ph-title">{title}</span>
      </div>
      <div style={{ padding: "18px 22px" }}>{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, paddingBottom: 16, marginBottom: 16, borderBottom: "1px solid var(--border)" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fs12 fw6" style={{ marginBottom: 2 }}>{label}</div>
        {hint && <div className="fs10 tc3">{hint}</div>}
      </div>
      <div style={{ flexShrink: 0, minWidth: 220 }}>{children}</div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 40,
        height: 22,
        borderRadius: 11,
        background: value ? "var(--flame)" : "var(--elevated)",
        border: `1px solid ${value ? "var(--flame)" : "var(--border)"}`,
        position: "relative",
        cursor: "pointer",
        transition: "background .2s, border-color .2s",
        boxShadow: value ? "0 0 10px rgba(255,79,23,.3)" : "none",
      }}
    >
      <div style={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        background: "#fff",
        position: "absolute",
        top: 2,
        left: value ? 20 : 2,
        transition: "left .2s",
        boxShadow: "0 1px 4px rgba(0,0,0,.3)",
      }} />
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      className="fi"
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ fontSize: 12 }}
    />
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select className="fsel" value={value} onChange={(e) => onChange(e.target.value)} style={{ fontSize: 12 }}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function AccountSettingsPage({ showToast: toast }) {
  const [form, setForm] = useState(() => {
    try {
      const stored = localStorage.getItem("theme");
      return { ...LOGGED_IN, theme: stored || LOGGED_IN.theme };
    } catch (e) {
      return LOGGED_IN;
    }
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("profile");

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  useEffect(() => {
    try {
      const theme = form?.theme || "light";
      const root = document.documentElement;
      if (theme === "light") root.classList.add("light-theme");
      else root.classList.remove("light-theme");
      localStorage.setItem("theme", theme);
    } catch (e) {
      // noop in non-browser environments
    }
  }, [form.theme]);

  const handleSave = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 900));
    setSaving(false);
    toast("Settings saved successfully", "s");
  };

  const nav = [
    { id: "profile", icon: "bi-person-circle", label: "Profile" },
    { id: "notifications", icon: "bi-bell", label: "Notifications" },
    { id: "appearance", icon: "bi-palette", label: "Appearance" },
    { id: "security", icon: "bi-shield-lock", label: "Security" },
  ];

  return (
    <div className="page">
      {/* ── page header ── */}
      <div style={{ marginBottom: 22 }}>
        <div className="syne fw7" style={{ fontSize: 20, marginBottom: 4 }}>Account Settings</div>
        <div className="fs11 tc3">Manage your personal preferences, notifications, and security settings.</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 20, alignItems: "start" }}>
        {/* ── left nav ── */}
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", position: "sticky", top: 0 }}>
          {/* user card */}
          <div style={{ padding: "16px 16px 14px", borderBottom: "1px solid var(--border)", textAlign: "center" }}>
            <div style={{
              width: 52, height: 52, borderRadius: "50%",
              background: "linear-gradient(135deg,var(--blue),var(--teal))",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16, fontWeight: 700, color: "var(--deep)",
              margin: "0 auto 10px", boxShadow: "0 0 20px rgba(64,196,255,.25)",
            }}>
              {form.avatar}
            </div>
            <div className="fs12 fw6">{form.name}</div>
            <div className="fs10 tc3" style={{ marginTop: 2 }}>{form.role}</div>
          </div>

          {/* nav links */}
          {nav.map(({ id, icon, label }) => (
            <div
              key={id}
              onClick={() => setActiveSection(id)}
              style={{
                display: "flex", alignItems: "center", gap: 9,
                padding: "10px 16px", cursor: "pointer",
                borderLeft: `2px solid ${activeSection === id ? "var(--flame)" : "transparent"}`,
                background: activeSection === id ? "rgba(255,79,23,.07)" : "transparent",
                transition: "all .13s",
              }}
            >
              <i className={`bi ${icon}`} style={{ fontSize: 14, color: activeSection === id ? "var(--flame)" : "var(--text3)", width: 16, textAlign: "center" }} />
              <span style={{ fontSize: 12, fontWeight: 500, color: activeSection === id ? "var(--flame)" : "var(--text2)" }}>{label}</span>
            </div>
          ))}

          {/* danger zone */}
          <div style={{ borderTop: "1px solid var(--border)", padding: 12 }}>
            <button
              className="btn btn-d btn-sm"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={() => toast("Sign out initiated", "w")}
            >
              <i className="bi bi-box-arrow-right" /> Sign Out
            </button>
          </div>
        </div>

        {/* ── right content ── */}
        <div>
          {/* PROFILE */}
          {activeSection === "profile" && (
            <Section title="Profile Information" icon="bi-person-circle">
              <Field label="Full Name" hint="Your display name across the system">
                <TextInput value={form.name} onChange={set("name")} />
              </Field>
              <Field label="Email Address" hint="Used for login and notifications">
                <TextInput value={form.email} onChange={set("email")} type="email" />
                </Field>
              <Field label="Language" hint="Interface language preference">
                <SelectInput value={form.language} onChange={set("language")} options={[
                  ["en-ZA", "English (South Africa)"],
                  ["af", "Afrikaans"],
                  ["zu", "isiZulu"],
                ]} />
              </Field>
              <div style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
                <Field label="Timezone" hint="Used for scheduling and timestamps">
                  <SelectInput value={form.timezone} onChange={set("timezone")} options={[
                    ["Africa/Johannesburg", "Africa/Johannesburg (SAST)"],
                    ["UTC", "UTC"],
                    ["Europe/London", "Europe/London"],
                  ]} />
                </Field>
              </div>
            </Section>
          )}

          {/* NOTIFICATIONS */}
          {activeSection === "notifications" && (
            <Section title="Notification Preferences" icon="bi-bell">
              {[
                { key: "emailNotifs", label: "Email Notifications", hint: "Receive updates via email" },
                { key: "weeklyReport", label: "Weekly Summary Report", hint: "Auto-generated performance digest every Monday" },
                { key: "newOrderAlerts", label: "New Order Alerts", hint: "Notify when a new order is placed" },
              ].map(({ key, label, hint }, i, arr) => (
                <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: i < arr.length - 1 ? 16 : 0, marginBottom: i < arr.length - 1 ? 16 : 0, borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <div>
                    <div className="fs12 fw6" style={{ marginBottom: 2 }}>{label}</div>
                    <div className="fs10 tc3">{hint}</div>
                  </div>
                  <Toggle value={form[key]} onChange={set(key)} />
                </div>
              ))}
            </Section>
          )}

          {/* APPEARANCE */}
          {activeSection === "appearance" && (
            <Section title="Appearance & Display" icon="bi-palette">
              <Field label="Theme" hint="Light or dark interface">
                <div className="fc gap8">
                  {["dark", "light"].map((t) => (
                    <div
                      key={t}
                      onClick={() => set("theme")(t)}
                      style={{
                        flex: 1, padding: "8px 0", borderRadius: 8, textAlign: "center",
                        background: form.theme === t ? "rgba(255,79,23,.12)" : "var(--deep)",
                        border: `1px solid ${form.theme === t ? "var(--flame)" : "var(--border)"}`,
                        cursor: "pointer", fontSize: 11, fontWeight: 600,
                        color: form.theme === t ? "var(--flame)" : "var(--text3)",
                        transition: "all .14s",
                      }}
                    >
                      <i className={`bi ${t === "dark" ? "bi-moon" : "bi-sun"}`} style={{ marginRight: 5 }} />
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </div>
                  ))}
                </div>
              </Field>
              <Field label="Default Landing Page" hint="Where you land after login">
                <SelectInput value={form.defaultView} onChange={set("defaultView")} options={[
                  ["dashboard", "Dashboard"],
                  ["orders", "Orders"],
                  ["customers", "Customers"],
                  ["collection", "Collection"],
                  ["accounts", "Accounts"],
                ]} />
              </Field>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div className="fs12 fw6" style={{ marginBottom: 2 }}>Compact Mode</div>
                  <div className="fs10 tc3">Reduce spacing for denser data views</div>
                </div>
                <Toggle value={form.compactMode} onChange={set("compactMode")} />
              </div>
            </Section>
          )}

          {/* SECURITY */}
          {activeSection === "security" && (
            <Section title="Security Settings" icon="bi-shield-lock">
              <Field label="Two-Factor Authentication" hint="Adds a second verification step on login">
                <div className="fc gap10">
                  <Toggle value={form.twoFactor} onChange={set("twoFactor")} />
                  {form.twoFactor && <span className="fs10" style={{ color: "var(--success)" }}>● Enabled</span>}
                </div>
              </Field>
              <Field label="Change Password" hint="Last changed 45 days ago">
                <button className="btn btn-s btn-sm" onClick={() => toast("Password reset email sent", "s")}>
                  <i className="bi bi-key" /> Send Reset Link
                </button>
              </Field>
              <Field label="Active Sessions" hint="Devices currently signed in">
                <button className="btn btn-d btn-sm" onClick={() => toast("All other sessions terminated", "w")}>
                  <i className="bi bi-x-circle" /> Revoke All Sessions
                </button>
              </Field>
              <div>
                <div className="fs12 fw6" style={{ marginBottom: 8 }}>Login History</div>
                {[
                  { device: "Chrome · Windows", location: "Johannesburg, ZA", time: "Today, 08:41" },
                  { device: "Safari · iPhone", location: "Sandton, ZA", time: "Yesterday, 19:12" },
                  { device: "Chrome · Windows", location: "Johannesburg, ZA", time: "2 days ago, 07:55" },
                ].map((s, i) => (
                  <div key={i} className="fc gap10" style={{ padding: "8px 12px", background: "var(--deep)", border: "1px solid var(--border)", borderRadius: 8, marginBottom: 6 }}>
                    <i className="bi bi-laptop" style={{ color: "var(--text3)", fontSize: 13 }} />
                    <div style={{ flex: 1 }}>
                      <div className="fs11 fw6">{s.device}</div>
                      <div className="fs10 tc3">{s.location} · {s.time}</div>
                    </div>
                    {i === 0 && <span className="fs9 fw7" style={{ color: "var(--success)", background: "rgba(0,230,118,.1)", border: "1px solid rgba(0,230,118,.2)", padding: "2px 7px", borderRadius: 10 }}>Current</span>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── save bar ── */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 4 }}>
            <button className="btn btn-g" onClick={() => { setForm(LOGGED_IN); toast("Changes discarded", "w"); }}>
              Discard
            </button>
            <button className="btn btn-p" onClick={handleSave} disabled={saving}>
              {saving ? <><i className="bi bi-arrow-repeat spin" /> Saving…</> : <><i className="bi bi-check2" /> Save Changes</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountSettingsPage;